import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../../api/client';
import { useAdminAuth } from '../../../app/providers/useAdminAuth';
import { Button } from '../../../shared/ui/Button';
import { Card } from '../../../shared/ui/Card';
import { Field } from '../../../shared/ui/Field';
import { Input } from '../../../shared/ui/Input';
import { useAdminLogin } from '../hooks/useAdminLogin';
import { useVerifyMfaLogin } from '../hooks/useVerifyMfaLogin';
import styles from './LoginPage.module.css';

// PARA-106, AC-1/AC-E1/AC-E2: форма логин/пароль -> /dashboard (200) или
// сообщение об ошибке, форма остаётся (401).
// PARA-151, AC-4/AC-5/AC-E3/AC-E4/AC-E5: при включённой MFA — второй шаг с
// 6-значным TOTP-кодом. 401 на верификации — generic-ошибка (неверный код и
// недействительный mfa_token неразличимы), остаёмся на шаге TOTP, есть
// видимый возврат "назад к паролю" (AC-E4). 429 — сообщение о rate-limit.
export function LoginPage(): JSX.Element {
  const { t } = useTranslation();
  const { status, login: setAuthenticated } = useAdminAuth();
  const loginMutation = useAdminLogin();
  const verifyMutation = useVerifyMfaLogin();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');

  if (status === 'authenticated') {
    return <Navigate to="/dashboard" replace />;
  }

  const handlePasswordSubmit = (event: FormEvent): void => {
    event.preventDefault();
    loginMutation.mutate(
      { login, password },
      {
        onSuccess: (response) => {
          if ('mfa_required' in response) {
            setMfaToken(response.mfa_token);
            setTotpCode('');
          } else {
            setAuthenticated(response.admin_access_token, response.admin_id);
          }
        },
      },
    );
  };

  const handleTotpSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (mfaToken === null) {
      return;
    }
    verifyMutation.mutate(
      { mfaToken, totpCode },
      {
        onSuccess: (response) => setAuthenticated(response.admin_access_token, response.admin_id),
      },
    );
  };

  const backToPasswordStep = (): void => {
    setMfaToken(null);
    setTotpCode('');
    loginMutation.reset();
    verifyMutation.reset();
  };

  if (mfaToken !== null) {
    const verifyStatus =
      verifyMutation.error instanceof ApiError ? verifyMutation.error.status : null;
    return (
      <div className={styles.screen}>
        <Card className={styles.card}>
          <form className={styles.form} onSubmit={handleTotpSubmit}>
            <h1 className={styles.title}>{t('login.mfaTitle')}</h1>
            <Field label={t('login.mfaCode')} htmlFor="totp-code">
              <Input
                id="totp-code"
                value={totpCode}
                onChange={(event) => setTotpCode(event.target.value)}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoComplete="one-time-code"
              />
            </Field>
            <Button type="submit" fullWidth disabled={verifyMutation.isPending}>
              {t('login.mfaSubmit')}
            </Button>
            {verifyMutation.isError && (
              <p className={styles.error} role="alert">
                {verifyStatus === 429 ? t('login.mfaRateLimited') : t('login.mfaError')}
              </p>
            )}
            <Button
              variant="ghost"
              fullWidth
              onClick={backToPasswordStep}
              disabled={verifyMutation.isPending}
            >
              {t('login.mfaBack')}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.screen}>
      <Card className={styles.card}>
        <form className={styles.form} onSubmit={handlePasswordSubmit}>
          <h1 className={styles.title}>{t('login.title')}</h1>
          <Field label={t('login.login')} htmlFor="login">
            <Input
              id="login"
              value={login}
              onChange={(event) => setLogin(event.target.value)}
              autoComplete="username"
            />
          </Field>
          <Field label={t('login.password')} htmlFor="password">
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
            />
          </Field>
          <Button type="submit" fullWidth disabled={loginMutation.isPending}>
            {t('login.submit')}
          </Button>
          {loginMutation.isError && (
            <p className={styles.error} role="alert">
              {t('login.error')}
            </p>
          )}
        </form>
      </Card>
    </div>
  );
}
